import { FC, Fragment, useEffect, useState } from "react";

import { Box, keyframes } from "@mui/material";

import { buildCaptionSegments, isUnbreakableHighlight, stripTrailingDots } from "./helpers/buildCaptionSegments";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";

const dotsKeyframes = keyframes`
  0% { content: "\\00A0"; }
  25% { content: "."; }
  50% { content: ".."; }
  75%, 100% { content: "..."; }
`;

const FADE_RISE_DURATION = "0.7s";
const FADE_RISE_EASING = "cubic-bezier(0.2, 0.7, 0.2, 1)";

const CAPTION_TEXT_COLOR = "#0f0e5b";
const CHIP_BACKGROUND_COLOR = "rgba(124, 92, 255, 0.14)";
const CHIP_TEXT_COLOR = "#4a3bc4";

export interface AnimatedCaptionProps {
  text: string;
  highlights: string[];
}

export const AnimatedCaption: FC<AnimatedCaptionProps> = ({ text, highlights }) => {
  const { value: prefersReducedMotion } = useMediaQuery("(prefers-reduced-motion: reduce)");

  const [shown, setShown] = useState(prefersReducedMotion);

  useEffect(() => {
    if (prefersReducedMotion) {
      setShown(true);
      return;
    }
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, [prefersReducedMotion]);

  const segments = buildCaptionSegments(stripTrailingDots(text), highlights);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: 84,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: 1,
        textAlign: "center",
        marginTop: "-50px",
      }}
    >
      <Box
        component="p"
        sx={{
          margin: 0,
          maxWidth: "90%",
          fontFamily: "Poppins, sans-serif",
          fontWeight: 500,
          fontSize: "26px",
          lineHeight: 1.3,
          letterSpacing: "-0.005em",
          color: CAPTION_TEXT_COLOR,
          opacity: shown ? 1 : 0,
          transform: shown ? "translateY(0)" : "translateY(14px)",
          transition: prefersReducedMotion
            ? "none"
            : `opacity ${FADE_RISE_DURATION} ${FADE_RISE_EASING}, transform ${FADE_RISE_DURATION} ${FADE_RISE_EASING}`,
        }}
      >
        {segments.map((segment, index) =>
          segment.isHighlight ? (
            <Box
              key={index}
              component="span"
              sx={{
                ...(isUnbreakableHighlight(segment.text)
                  ? {
                      display: "inline-block",
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      verticalAlign: "bottom",
                    }
                  : {
                      display: "inline",
                      WebkitBoxDecorationBreak: "clone",
                      boxDecorationBreak: "clone",
                    }),
                borderRadius: "999px",
                padding: "0.04em 0.36em",
                margin: "0 0.05em",
                backgroundColor: shown ? CHIP_BACKGROUND_COLOR : "transparent",
                color: shown ? CHIP_TEXT_COLOR : "inherit",
                transition: prefersReducedMotion
                  ? "none"
                  : "background-color 0.45s ease-out 0.15s, color 0.45s ease-out 0.15s",
              }}
            >
              {segment.text}
            </Box>
          ) : (
            <Fragment key={index}>{segment.text}</Fragment>
          )
        )}
        <Box
          component="span"
          aria-hidden
          sx={{
            display: "inline-block",
            width: "1.4em",
            textAlign: "left",
            "&::after": {
              content: '"\\00A0"',
              ...(prefersReducedMotion
                ? { content: '"..."' }
                : { animation: `${dotsKeyframes} 1.4s steps(4, end) 0.7s infinite` }),
            },
          }}
        />
      </Box>
    </Box>
  );
};
